import type { CompanyProfile } from "../types";
import type {
  EffectivePurchaseOrderDraft,
  PurchaseOrderHeader,
} from "./purchaseOrderEdits";
import { formatJpDateFixed, transitionalSummary } from "./invoiceTransition";
import {
  JP_REGISTRATION_CONCEPT,
  type RegistrationConcept,
} from "./registrationLabel";

import { t } from "../lib/i18n";
import { roundMoney } from "./money";

/** Excel が UTF-8 CSV を文字化けさせないための BOM。 */
const UTF8_BOM = "﻿";

/** RFC 4180 準拠のフィールドエスケープ（カンマ・改行・二重引用符を含む場合に引用）。 */
function escapeField(value: string | number): string {
  const s = String(value);
  if (/[",\r\n]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

function toRow(fields: (string | number)[]): string {
  return fields.map(escapeField).join(",");
}

/** その発注書の通貨で金額列を丸める（JPY=整数 / USD=セントまで）。 */
function amountIn(draft: { currency: string }) {
  return (amount: string | number): number => roundMoney(amount, draft.currency);
}

/**
 * 1 仕入先分の発注書ドラフトを、明細＋税区分別集計を含む CSV 文字列に変換する。
 * 先頭に UTF-8 BOM を付与し、Excel でそのまま開ける。発注元（自社プロフィール）が
 * 設定されていれば、宛先の前に発注元情報を出力する。
 */
export function buildPurchaseOrderCsv(
  draft: EffectivePurchaseOrderDraft,
  company?: CompanyProfile,
  header?: PurchaseOrderHeader,
  destination?: string,
  options?: {
    /**
     * 適格請求書の経過措置の行を出すか（既定 true）。
     * 🔴 経過措置は日本の制度＝JP 以外の店の CSV に出すと、存在しない制度の
     * 控除額を仕入先向けの書類に断言することになる。呼び出し側が店の国で決める
     * （PDF の `showInvoiceTransitionNote` と同じゲート）。
     */
    showInvoiceTransitionNote?: boolean;
    /**
     * 登録番号のラベル（国の概念・Step 3）。未指定＝JP（後方互換）・null＝
     * 概念なし（US 等＝発注元・仕入先の登録番号行を出さない）。PDF と同じ規則。
     */
    registration?: RegistrationConcept | null;
  },
): string {
  const registration =
    options?.registration === undefined
      ? JP_REGISTRATION_CONCEPT
      : options.registration;
  const rows: string[] = [];
  // 🔴 税率行が1つも無い店（US・カタログ外の国＝Step 4）は税区分列・税集計・
  //    「税込」の文言を書類から消す＝「税の行が存在しない」が正しい形。
  const hasTax = draft.tax_groups.length > 0;

  const metaRows: string[] = [];
  if (header?.issueDate) metaRows.push(toRow([t("発行日"), header.issueDate]));
  if (header?.poNumber) metaRows.push(toRow([t("発注番号"), header.poNumber]));
  if (header?.desiredDeliveryDate) {
    metaRows.push(toRow([t("希望納品日"), header.desiredDeliveryDate]));
  }
  if (destination) metaRows.push(toRow([t("配送先"), destination]));
  if (metaRows.length > 0) {
    rows.push(...metaRows);
    rows.push("");
  }

  if (company?.company_name) {
    rows.push(toRow([t("発注元"), company.company_name]));
    // 登録番号の行は国の概念があるときだけ。JP＝従来ラベル・非 JP＝国の呼び名。
    if (company.company_registration_no && registration) {
      rows.push(
        toRow([
          registration.isJapanInvoiceSystem
            ? t("発注元 登録番号")
            : registration.label,
          company.company_registration_no,
        ]),
      );
    }
    rows.push("");
  }

  const roundYen = amountIn(draft);
  rows.push(toRow([t("仕入先"), draft.supplier_name]));
  // JP＝未登録も明示する（経過措置の対象＝空欄と区別が要る）。非 JP＝値がある
  // ときだけその国のラベルで出す。概念なし（US 等）＝行ごと出さない。
  if (registration?.isJapanInvoiceSystem) {
    rows.push(
      toRow([t("仕入先 登録番号"), draft.invoice_registration_no ?? t("未登録")]),
    );
  } else if (registration && draft.invoice_registration_no) {
    rows.push(toRow([registration.label, draft.invoice_registration_no]));
  }
  rows.push("");

  rows.push(
    toRow([
      "SKU",
      t("商品名"),
      t("在庫"),
      t("発注数"),
      t("仕入単価"),
      ...(hasTax ? [t("税区分")] : []),
      t("小計"),
    ]),
  );
  for (const line of draft.lines) {
    rows.push(
      toRow([
        line.sku ?? "",
        line.title ?? "",
        line.available,
        line.recommended_qty,
        roundYen(line.unit_price),
        // 税率名はマーチャントの設定文字列＝訳さない。
        ...(hasTax ? [line.tax_rate_name ?? ""] : []),
        roundYen(line.line_amount),
      ]),
    );
  }

  rows.push("");
  // 🔴 税率ごとに1組（使われていない税率の 0 行は出さない）。
  for (const group of draft.tax_groups) {
    rows.push(toRow([group.name, roundYen(group.subtotal)]));
    rows.push(toRow([t("うち税"), roundYen(group.tax)]));
  }
  rows.push(toRow([hasTax ? t("合計（税込）") : t("合計"), roundYen(draft.total_amount)]));

  // 未登録仕入先は経過措置の対象。発行日基準の控除割合と控除可能/不可の消費税額を追記する。
  if (
    (options?.showInvoiceTransitionNote ?? true) &&
    !draft.invoice_registration_no &&
    header?.issueDate
  ) {
    const summary = transitionalSummary(
      draft.tax_groups.reduce((sum, g) => sum + roundYen(g.tax), 0),
      header.issueDate,
    );
    rows.push("");
    rows.push(toRow([t("経過措置 控除割合"), `${summary.measure.ratePercent}%`]));
    rows.push(toRow([t("控除可能 消費税"), summary.split.deductible]));
    rows.push(toRow([t("控除不可 消費税"), summary.split.nonDeductible]));
    if (summary.measure.next) {
      // 🔴 帳票の日付は店の国で決める（Step 3）＝経過措置は JP の店にしか出ないので
      //    常に和風固定。UI ロケールが en でも仕入先向け書類の日付は変えない。
      rows.push(
        toRow([
          t("次の引き下げ"),
          `${formatJpDateFixed(summary.measure.next.startDate)} ${summary.measure.next.ratePercent}%`,
        ]),
      );
    }
  }

  if (header?.note) {
    rows.push("");
    rows.push(toRow([t("備考"), header.note]));
  }

  return UTF8_BOM + rows.join("\r\n");
}

/** 純正取込で照合できる明細か（SKU かバーコードのどちらかが必要）。 */
function hasShopifyPoMatchKey(
  line: EffectivePurchaseOrderDraft["lines"][number],
): boolean {
  return (line.sku ?? "").trim() !== "" || (line.barcode ?? "").trim() !== "";
}

/**
 * Shopify注文書用CSV に含められない明細数（SKU・バーコード両方未設定で発注数 > 0）。
 * 「黙って行が欠ける」とマーチャントが取り込み後まで気付けないため、
 * ダウンロード時の警告表示に使う。
 */
export function countShopifyPoImportSkippedLines(
  draft: EffectivePurchaseOrderDraft,
): number {
  return draft.lines.filter(
    (line) => line.recommended_qty > 0 && !hasShopifyPoMatchKey(line),
  ).length;
}

/** 明細の税率 → Shopify 純正 PO の Tax 列（税率パーセントの数値）。 */
function shopifyTaxPercent(line: { tax_rate: string | null }): number {
  return Number(line.tax_rate ?? 0) * 100;
}

/** Shopify 純正 PO インポートのヘッダ（列順は公式サンプルと逐語一致）。 */
const SHOPIFY_PO_IMPORT_HEADER = [
  "SKU",
  "Barcode",
  "Supplier SKU",
  "Quantity",
  "Cost",
  "Tax",
];

/**
 * 発注書ドラフトを Shopify 純正の「注文書（PO）」CSV インポート形式に変換する。
 *
 * 公式サンプル（help.shopify.com/csv/purchase_order_template.csv）と同じ
 * `SKU,Barcode,Supplier SKU,Quantity,Cost,Tax` のフラット表で、**BOM は付けない**
 * （先頭 "SKU" が壊れて純正インポートが弾くため）。仕入先向けの発注書ドキュメント
 * （`buildPurchaseOrderCsv`）とは別物＝こちらは純正 PO へ流し込む取込専用。
 *
 * - 純正は SKU **または** バーコードで照合する（公式テンプレにバーコードのみの行が
 *   ある）ため、SKU・バーコードの**両方が無い**明細と発注数 0 の明細だけ出さない。
 *   SKU 未設定で JAN（バーコード）のみの商品もバーコード照合で取り込める。
 * - Supplier SKU はアプリ側に無いので空。Cost は仕入単価（店の通貨の最小単位で丸め）、
 *   Tax は税率パーセント（軽減8／標準10）。
 */
export function buildShopifyPoImportCsv(draft: EffectivePurchaseOrderDraft): string {
  const roundYen = amountIn(draft);
  const rows: string[] = [toRow(SHOPIFY_PO_IMPORT_HEADER)];
  for (const line of draft.lines) {
    if (!hasShopifyPoMatchKey(line) || line.recommended_qty <= 0) continue;
    rows.push(
      toRow([
        (line.sku ?? "").trim(),
        (line.barcode ?? "").trim(),
        "", // Supplier SKU（アプリ未保有）
        line.recommended_qty,
        roundYen(line.unit_price),
        shopifyTaxPercent(line),
      ]),
    );
  }
  // 純正取込用は BOM なし・CRLF 区切り（Excel 表示用ではなく機械取込用）。
  return rows.join("\r\n");
}

/** ファイル名に使えない文字を安全な形に整える。 */
function sanitizeFilename(name: string): string {
  return name.replace(/[\\/:*?"<>|]/g, "_").trim() || "supplier";
}

export function purchaseOrderCsvFilename(
  draft: EffectivePurchaseOrderDraft,
  today: string,
): string {
  return `${t("発注書")}_${sanitizeFilename(draft.supplier_name)}_${today}.csv`;
}

export function shopifyPoImportCsvFilename(
  draft: EffectivePurchaseOrderDraft,
  today: string,
): string {
  return `${t("Shopify注文書")}_${sanitizeFilename(draft.supplier_name)}_${today}.csv`;
}
